// マスタCSV取込（純関数・中身/TMS）。DENEB等の車両/乗務員ダンプ(cp932/UTF-8)を
// 車両台帳(vehicles)・乗務員台帳(drivers)へ。ヘッダ名解決→キー正規化→既存と照合し
// 新規/補完(enrich)/スキップに分類。Firebase非依存＝Nodeでテスト可。
import { lastNumberGroup } from './helpers.js'

const norm = (v) => String(v == null ? '' : v).trim()
// 車番キー：先頭ゼロ・「.0」を除去（vehicles.js の normPlate と一致）
export const normPlate = (v) => norm(v).replace(/\.0+$/, '').replace(/^0+(?=\d)/, '')
// 乗務員コード：先頭ゼロ除去（drivers.js の normCode と一致）
export const normCode = (v) => norm(v).replace(/^0+(?=\d)/, '')
const allZero = (s) => s !== '' && /^0+$/.test(s)
const pick = (rec, names) => { for (const n of names) { if (rec && n in rec && norm(rec[n]) !== '') return norm(rec[n]) } return '' }

const VEH_FIELDS = {
  carNo: ['車両番号', '車番', '管理番号', '社番', '車輌番号'],
  plate: ['車両名称', '登録ナンバー', 'ナンバー', '登録番号', '車両名', '車輌名称'],
  klass: ['車種', '車種名称', '車格', '車両種別', '料金車種区分'],
  depot: ['所属名称', '営業所', '所属', '所属名称1'],
}
const DRV_FIELDS = {
  code: ['乗務員コード', '社員コード', '乗務員CD', 'コード', '社員番号'],
  name: ['乗務員名', '氏名', '名前', '乗務員氏名', 'ドライバー名'],
  kana: ['乗務員名カナ', 'カナ', 'フリガナ', '氏名カナ'],
  depot: ['所属名称1', '所属名称', '営業所', '所属'],
}

// 車種テキスト → 台帳の klass（large/medium）。不明は '' （新規時に medium 既定）。
function klassOf(s) {
  const t = norm(s)
  if (!t) return ''
  if (/大型|10\s*t|13\s*t|増ト/i.test(t)) return 'large'
  if (/中型|4\s*t/i.test(t)) return 'medium'
  return ''
}

// 車両レコード群 → 候補[{id, carNo, name, depot, klass, updatedAt, srcNo}]
// キー＝正規化した「車両番号」（デジタコ／DENEB共通の車両ID）。プレート末尾はキーに使わない
// （末尾数字は営業所違いの別車で衝突＝別車を誤統合するため。例: 滋賀106う3 と 京都115あ3）。
// プレート（車両名称）は表示名としてのみ採用。ダミー「…0000」/空は名称なし。
export function mapVehicles(records) {
  const out = new Map()
  for (const rec of records) {
    const id = normPlate(pick(rec, VEH_FIELDS.carNo))
    if (!id) continue
    const plate = pick(rec, VEH_FIELDS.plate)
    const tail = lastNumberGroup(plate)
    const dummy = plate === '' || allZero(tail)  // "京都130か0000" 等は名称なし扱い
    const cand = {
      id, carNo: id,
      name: dummy ? '' : plate,
      depot: pick(rec, VEH_FIELDS.depot),
      klass: klassOf(pick(rec, VEH_FIELDS.klass)),
      updatedAt: pick(rec, ['更新日', '更新日時']),
      srcNo: pick(rec, ['項', 'No', 'no']),
    }
    const prev = out.get(id)
    if (!prev) out.set(id, cand)
    else out.set(id, { ...prev, name: prev.name || cand.name, depot: prev.depot || cand.depot, klass: prev.klass || cand.klass })
  }
  return [...out.values()]
}

// 乗務員レコード群 → 候補[{id, code, name, kana, depot, status, updatedAt}]
export function mapDrivers(records) {
  const out = new Map()
  for (const rec of records) {
    const code = normCode(pick(rec, DRV_FIELDS.code))
    if (!code) continue
    if (out.has(code)) continue
    const depot = pick(rec, DRV_FIELDS.depot)
    out.set(code, {
      id: code, code,
      name: pick(rec, DRV_FIELDS.name),
      kana: pick(rec, DRV_FIELDS.kana),
      depot,
      // 所属なし(無所属/空)＝現役でない → 既定は退職。所属ありは在籍。
      status: (depot === '' || depot === '無所属') ? 'inactive' : 'active',
      updatedAt: pick(rec, ['更新日', '更新日時']),
    })
  }
  return [...out.values()]
}

// 既存マスタ(existing: id→doc)と照合して分類（非破壊）。
// enrich の patch は「既存が空の識別項目のみ補完」＝車種/日額/給与/状態/備考等は絶対に触らない。
export function planVehicles(cands, existing = {}) {
  const nm = (s) => String(s == null ? '' : s).trim()
  // 既存の プレート→id 逆引き（同一車をナンバーで突合＝1000xxxx合成コードの二重登録を防ぐ）
  const byPlate = {}
  for (const id in existing) { const p = nm(existing[id] && existing[id].name); if (p && !(p in byPlate)) byPlate[p] = id }
  const used = new Set(Object.keys(existing))
  return cands.map((c) => {
    const plate = nm(c.name)
    let targetId = null
    // ① 車両番号キー一致（かつプレート非矛盾＝同一車） ② プレート一致（既存の別キーに紐付く）
    if (existing[c.id]) { const exp = nm(existing[c.id].name); if (!plate || !exp || exp === plate) targetId = c.id }
    if (!targetId && plate && byPlate[plate]) targetId = byPlate[plate]
    if (targetId) {
      const ex = existing[targetId]
      const patch = {}
      if (!nm(ex.name) && c.name) patch.name = c.name
      return { ...c, id: targetId, carNo: ex.carNo || targetId, matchedName: nm(ex.name) || c.name, kind: Object.keys(patch).length ? 'enrich' : 'skip', patch }
    }
    // ③ 新規：末尾番号→車両番号→サフィックス の順で必ず一意キー（既存を絶対に上書きしない）
    const tail = normPlate(lastNumberGroup(c.name))
    let key = (tail && !used.has(tail)) ? tail : (!used.has(c.id) ? c.id : '')
    if (!key) { let n = 2; while (used.has(c.id + '-' + n)) n++; key = c.id + '-' + n }
    used.add(key)
    return { ...c, id: key, carNo: key, kind: 'new' }
  })
}
export function planDrivers(cands, existing = {}) {
  return cands.map((c) => {
    const ex = existing[c.id]
    if (!ex) return { ...c, kind: 'new' }
    const patch = {}
    if ((!ex.name || ex.name === '') && c.name) patch.name = c.name
    if ((!ex.kana || ex.kana === '') && c.kana) patch.kana = c.kana
    return { ...c, kind: Object.keys(patch).length ? 'enrich' : 'skip', patch }
  })
}

export function summarize(planned) {
  const s = { total: planned.length, new: 0, enrich: 0, skip: 0 }
  for (const p of planned) s[p.kind]++
  return s
}
